// Apolo IDE — GuardrailsPanel (S4 · E5). SidePanel tab composing the checkpoint
// + auto-review surfaces. Decoupled from the loop: it listens to window
// CustomEvents that SessionWorkspace dispatches (apolo:autoReview / apolo:checkpoint),
// so there is no prop-drilling through the giant workspace render.

import { useEffect, useRef, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { CheckpointBar } from './CheckpointBar.jsx';
import { AutoReviewNotice } from './AutoReviewNotice.jsx';
import { AutoReviewMetricsIndicator } from './AutoReviewMetricsIndicator.jsx';

const sectionLabel = {
  fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: 'var(--rx-tx3)', margin: '0 0 8px',
};

export default function GuardrailsPanel({ sessionId }) {
  const [checkpoints, setCheckpoints] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [notices, setNotices] = useState([]);
  const noticeSeq = useRef(0);

  // Reset surfaces when the active session changes.
  useEffect(() => {
    setCheckpoints([]);
    setMetrics(null);
    setNotices([]);
  }, [sessionId]);

  useEffect(() => {
    const inSession = (d) => !d?.sessionId || !sessionId || d.sessionId === sessionId;

    function onReview(e) {
      const d = e.detail || {};
      if (!inSession(d)) return;
      if (d.metrics) setMetrics(d.metrics);
      if (d.decision) {
        noticeSeq.current += 1;
        const entry = { key: noticeSeq.current, decision: d.decision, name: d.name, reason: d.reason };
        setNotices((prev) => [entry, ...prev].slice(0, 20));
      }
    }
    function onCheckpoint(e) {
      const d = e.detail || {};
      if (!inSession(d)) return;
      if (Array.isArray(d.checkpoints)) setCheckpoints(d.checkpoints);
    }

    window.addEventListener('apolo:autoReview', onReview);
    window.addEventListener('apolo:checkpoint', onCheckpoint);
    return () => {
      window.removeEventListener('apolo:autoReview', onReview);
      window.removeEventListener('apolo:checkpoint', onCheckpoint);
    };
  }, [sessionId]);

  return (
    <div
      data-testid="guardrails-panel"
      role="region"
      aria-label="Guard-rails — Auto-review e Checkpoints"
      style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 16 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--rx-tx1)', fontSize: 12, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <ShieldCheck size={14} aria-hidden="true" style={{ color: 'var(--rx-accent)', flexShrink: 0 }} />
          Auto-review &amp; Checkpoints
        </div>
        <AutoReviewMetricsIndicator metrics={metrics} />
      </div>

      <section aria-label="Checkpoints">
        <h3 style={sectionLabel}>Checkpoints (estado verde)</h3>
        <CheckpointBar
          checkpoints={checkpoints}
          onRestore={(cp) => window.dispatchEvent(new CustomEvent('apolo:restoreCheckpoint', { detail: { sessionId, checkpoint: cp } }))}
        />
      </section>

      <section aria-label="Auto-review">
        <h3 style={sectionLabel}>Auto-review (ações revisadas)</h3>
        {notices.length === 0 ? (
          <div
            data-testid="auto-review-empty"
            style={{
              display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
              padding: '10px 12px', fontSize: 11, color: 'var(--rx-tx2)',
              border: '1px dashed var(--rx-border)', borderRadius: 10, background: 'var(--rx-s1)',
            }}
          >
            Nenhuma ação revisada ainda. Crie um <code className="rx-chip">.apollo/permissions.json</code> para ativar o gate.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {notices.map((n) => (
              <AutoReviewNotice key={n.key} decision={n.decision} name={n.name} reason={n.reason} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
